import { Box, Tab, Tabs as MuiTabs, Typography } from '@mui/material'
import { useCurrentUser } from 'app/auth'
import { paths } from 'app/routes'
import { TabPanel } from 'elements/navigation'
import { useTranslation } from 'react-i18next'
import { generatePath, Navigate, useSearchParams } from 'react-router-dom'

import { useProjectDetailsContext } from '../project-details-layout'
import {
  ProjectIntegrationsTabContent,
  ProjectLanguagesTabContent,
  ProjectSettingsTabContent,
} from './components/tabs'

export enum ProjectSettingsTab {
  Settings = 'settings',
  Integrations = 'integrations',
  Languages = 'languages',
}

export const ProjectSettingsPage = () => {
  const { t } = useTranslation()

  const { project } = useProjectDetailsContext()

  const [searchParams, setSearchParams] = useSearchParams()

  const tab = searchParams.get('tab') || ProjectSettingsTab.Settings

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: ProjectSettingsTab,
  ) => {
    setSearchParams(params => {
      params.set('tab', newValue)
      return params
    })
  }

  const { permission: currentUserPermission } = useCurrentUser()

  if (!Object.values(ProjectSettingsTab).includes(tab as ProjectSettingsTab)) {
    return (
      <Navigate
        replace
        to={{
          pathname: generatePath(paths.projectSettings, {
            projectId: project.projectId,
          }),
        }}
      />
    )
  }

  return (
    <Box px={5} py={3}>
      <Box width="100%">
        <Typography variant="h1">
          {t('projects.settings.project_settings')}
        </Typography>

        <Box width="100%" display="flex" justifyContent="stretch" py={4}>
          <Box>
            <MuiTabs
              variant="scrollable"
              orientation="vertical"
              value={tab}
              onChange={handleChange}
              indicatorColor="primary"
              TabIndicatorProps={{
                sx: {
                  width: 4,
                  left: 0,
                },
              }}
              sx={{
                width: 350,
                '& .MuiTab-root': {
                  px: 2,
                  mr: 1,
                  fontSize: theme => theme.typography.caption2,
                  alignItems: 'start',
                  mb: 2,
                  minHeight: 28,
                  maxHeight: 28,
                },
              }}
            >
              <Tab
                value={ProjectSettingsTab.Settings}
                disableRipple
                label={t('projects.settings.project_settings')}
              />

              {currentUserPermission !== 'translator' && (
                <Tab
                  value={ProjectSettingsTab.Integrations}
                  disableRipple
                  label={t('projects.settings.integrations.integrations')}
                />
              )}

              {currentUserPermission === 'owner' && (
                <Tab
                  value={ProjectSettingsTab.Languages}
                  disableRipple
                  label={t('projects.settings.languages')}
                />
              )}
            </MuiTabs>
          </Box>
          <Box width={1} maxWidth={800}>
            <TabPanel currentTab={tab} value={ProjectSettingsTab.Settings}>
              <ProjectSettingsTabContent
                project={project}
                isOwner={currentUserPermission === 'owner'}
              />
            </TabPanel>

            <TabPanel currentTab={tab} value={ProjectSettingsTab.Integrations}>
              <ProjectIntegrationsTabContent projectId={project.projectId} />
            </TabPanel>

            <TabPanel currentTab={tab} value={ProjectSettingsTab.Languages}>
              <ProjectLanguagesTabContent project={project} />
            </TabPanel>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
